// src/lib/resources/data/generators/colorPaletteGenerator.ts

import { 
  ToolType, 
  ToolCategories, 
  DifficultyLevel
} from '~/lib/resources/types/shared/enums'
import type { Generator } from '~/lib/resources/types/tools'

export const colorPaletteGenerator: Generator = {
  id: 'color-palette-generator',
  title: 'Color Palette Generator',
  canonicalTitle: 'Color Palette Generator',
  description: 'Create professional color palettes for your marketing campaigns, brand identity, and design projects. Generate harmonious color schemes with accessibility compliance and export options.',
  resourceType: 'tool',
  toolType: ToolType.GENERATOR,
  toolCategory: ToolCategories.CREATIVE,
  slug: 'color-palette-generator',
  category: 'Design Tools',
  featured: false,
  lastUpdated: '2026-05-29T13:13:26+00:00',
  estimatedTime: '3-5 min',
  difficulty: DifficultyLevel.BEGINNER,
  
  // Images
  images: {
    hero: {
      url: '/images/resources/tools/generators/adsights-color-palette-generator-design-tools-marketing-tools-hero-image_AR-16x9_2400x1350_2x.jpg',
      width: 2400,
      height: 1350,
      alt: 'Color Palette Generator tool showing customizable color schemes and accessibility compliance for marketing design'
    },
    thumbnail: {
      url: '/images/resources/tools/generators/adsights-color-palette-generator-design-tools-marketing-tools-og-image_AR-1x1.91_2400x1260_2x.jpg',
      width: 1200,
      height: 675,
      alt: 'Color Palette Generator tool showing customizable color schemes and accessibility compliance for marketing design'
    },
    og: {
      url: '/images/resources/tools/generators/adsights-color-palette-generator-design-tools-marketing-tools-og-image_AR-1x1.91_2400x1260_2x.jpg',
      width: 2400,
      height: 1260,
      alt: 'Color Palette Generator tool showing customizable color schemes and accessibility compliance for marketing design'
    }
  },
  
  // Related resources
  relatedTools: [
    'subtitle-overlay-generator', // Subtitle Overlay Generator
    'creative-quality-grader', // Creative Quality Grader
    'roas-calculator', // ROAS Calculator
    'ab-test-statistical-significance-calculator', // A/B Test Statistical Significance Calculator
  ],
  
  // Metadata
  metadata: {
    title: 'Color Palette Generator | Free Marketing Color Schemes',
    description: 'Generate professional color palettes for marketing campaigns, brand identity, and design projects. Free tool to create harmonious color schemes with accessibility compliance and export options.',
    keywords: [
      'color palette generator',
      'color scheme creator',
      'marketing color tool',
      'brand color generator',
      'color palette maker',
      'design color tool',
      'color harmony generator',
      'accessible color palette',
      'marketing design tool',
      'brand identity colors',
      'color combination tool',
      'palette generator',
      'color scheme maker',
      'design color palette',
      'marketing colors',
      'brand colors',
      'color wheel tool',
      'color harmony',
      'accessible colors',
      'color contrast checker'
    ],
    dateCreated: '2025-09-27T12:00:00Z',
    dateModified: '2026-05-29T13:13:26+00:00',
    canonicalURL: '/resources/tools/generators/color-palette-generator'
  },

  // Structured data (Schema.org)
  teaches: 'Color Theory, Brand Design, Accessibility Compliance, Marketing Design, Visual Identity',
  assesses: 'Color Palette Creation, Brand Design Skills, Accessibility Awareness, Design Aesthetics',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Marketing Design Tool',
  featureList: [
    'Interactive color palette builder with harmony rules',
    'Accessibility compliance checking',
    'Brand color integration',
    'Export capabilities',
    'Marketing-specific color schemes',
  ],
  interactivityType: 'active',
  softwareRequirements: 'Modern web browser with JavaScript enabled',
  browserRequirements: 'Chrome 80+, Firefox 75+, Safari 13.1+, or Edge 80+',
  
  // Stats
  stats: {
    views: 0,
    completions: 0,
    shares: 0
  },
  
  config: {
    steps: [
      // Step 1: Base Color
      {
        id: 'base-color',
        title: 'Base Color',
        description: 'Choose your primary color to build the palette around.',
        fields: [
          {
            id: 'baseColor',
            type: 'color',
            label: 'Primary Color',
            description: 'Select the main color for your palette. This will be used as the foundation for generating harmonious colors.',
            validation: {
              required: true
            },
            templateVar: 'baseColor',
            affectsPreview: true,
            defaultValue: '#3B82F6'
          },
          {
            id: 'colorName',
            type: 'text',
            label: 'Palette Name',
            description: 'Give your color palette a memorable name.',
            placeholder: 'e.g., Ocean Breeze, Sunset Vibes, Corporate Blue',
            validation: {
              required: true
            },
            templateVar: 'colorName',
            affectsPreview: true
          }
        ],
        template: `
          <div class="color-palette-base">
            <h2>{{colorName}}</h2>
            <div class="base-color" style="background-color: {{baseColor}};">
              <span class="color-hex">{{baseColor}}</span>
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="palette-preview">
              <h3>{{colorName}}</h3>
              <div class="color-swatch" style="background-color: {{baseColor}}; width: 100%; height: 60px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">
                {{baseColor}}
              </div>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 2: Color Harmony
      {
        id: 'color-harmony',
        title: 'Color Harmony',
        description: 'Choose the color harmony rule to generate your palette.',
        fields: [
          {
            id: 'harmonyType',
            type: 'select',
            label: 'Color Harmony Type',
            description: 'Select how colors should relate to each other for visual harmony.',
            validation: {
              required: true
            },
            templateVar: 'harmonyType',
            affectsPreview: true,
            options: [
              { id: 'monochromatic', label: 'Monochromatic', value: 'monochromatic' },
              { id: 'analogous', label: 'Analogous', value: 'analogous' },
              { id: 'complementary', label: 'Complementary', value: 'complementary' },
              { id: 'triadic', label: 'Triadic', value: 'triadic' },
              { id: 'tetradic', label: 'Tetradic', value: 'tetradic' },
              { id: 'split-complementary', label: 'Split Complementary', value: 'split-complementary' }
            ],
            defaultValue: 'monochromatic'
          },
          {
            id: 'paletteSize',
            type: 'select',
            label: 'Palette Size',
            description: 'How many colors should be in your palette?',
            validation: {
              required: true
            },
            templateVar: 'paletteSize',
            affectsPreview: true,
            options: [
              { id: '3', label: '3 Colors', value: '3' },
              { id: '4', label: '4 Colors', value: '4' },
              { id: '5', label: '5 Colors', value: '5' },
              { id: '6', label: '6 Colors', value: '6' }
            ],
            defaultValue: '4'
          }
        ],
        template: `
          <div class="color-harmony">
            <h3>{{harmonyType}} Harmony</h3>
            <p>Generating {{paletteSize}} colors using {{harmonyType}} color theory.</p>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="harmony-preview">
              <p class="text-sm text-gray-600 dark:text-gray-400">{{harmonyType}} harmony with {{paletteSize}} colors</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 3: Accessibility & Usage
      {
        id: 'accessibility-usage',
        title: 'Accessibility & Usage',
        description: 'Configure accessibility compliance and intended usage.',
        fields: [
          {
            id: 'accessibilityLevel',
            type: 'select',
            label: 'Accessibility Compliance',
            description: 'Choose the minimum contrast ratio for text readability.',
            validation: {
              required: true
            },
            templateVar: 'accessibilityLevel',
            affectsPreview: true,
            options: [
              { id: 'aa', label: 'WCAG AA (4.5:1)', value: 'aa' },
              { id: 'aaa', label: 'WCAG AAA (7:1)', value: 'aaa' },
              { id: 'custom', label: 'Custom (3:1)', value: 'custom' }
            ]
          },
          {
            id: 'usageType',
            type: 'multiselect',
            label: 'Intended Usage',
            description: 'What will this palette be used for?',
            validation: {
              required: true
            },
            templateVar: 'usageType',
            affectsPreview: true,
            options: [
              { id: 'brand', label: 'Brand Identity', value: 'brand' },
              { id: 'marketing', label: 'Marketing Campaigns', value: 'marketing' },
              { id: 'web', label: 'Web Design', value: 'web' },
              { id: 'print', label: 'Print Materials', value: 'print' },
              { id: 'social', label: 'Social Media', value: 'social' },
              { id: 'presentation', label: 'Presentations', value: 'presentation' }
            ]
          },
          {
            id: 'includeNeutrals',
            type: 'checkbox',
            label: 'Include Neutral Colors',
            description: 'Add black, white, and gray variations to the palette.',
            validation: {
              required: false
            },
            templateVar: 'includeNeutrals',
            affectsPreview: true,
            defaultValue: false
          }
        ],
        template: `
          <div class="accessibility-usage">
            <h3>Accessibility & Usage</h3>
            <p>WCAG {{accessibilityLevel}} compliance for {{usageType}} usage.</p>
            {{#if includeNeutrals}}<p>Including neutral colors for versatility.</p>{{/if}}
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="usage-preview">
              <p class="text-sm text-gray-600 dark:text-gray-400">WCAG {{accessibilityLevel}} • {{usageType}}</p>
              {{#if includeNeutrals}}<p class="text-xs text-gray-500">+ Neutral colors</p>{{/if}}
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 4: Customization
      {
        id: 'customization',
        title: 'Customization',
        description: 'Fine-tune your color palette with additional options.',
        fields: [
          {
            id: 'saturation',
            type: 'select',
            label: 'Color Saturation',
            description: 'How vibrant should the colors be?',
            validation: {
              required: true
            },
            templateVar: 'saturation',
            affectsPreview: true,
            options: [
              { id: 'original', label: 'Original (Keep Base Color)', value: 'original' },
              { id: 'muted', label: 'Muted (Subtle)', value: 'muted' },
              { id: 'balanced', label: 'Balanced', value: 'balanced' },
              { id: 'vibrant', label: 'Vibrant (Bold)', value: 'vibrant' }
            ],
            defaultValue: 'original'
          },
          {
            id: 'lightness',
            type: 'select',
            label: 'Color Lightness',
            description: 'How light or dark should the colors be?',
            validation: {
              required: true
            },
            templateVar: 'lightness',
            affectsPreview: true,
            options: [
              { id: 'original', label: 'Original (Keep Base Color)', value: 'original' },
              { id: 'dark', label: 'Dark', value: 'dark' },
              { id: 'medium', label: 'Medium', value: 'medium' },
              { id: 'light', label: 'Light', value: 'light' }
            ],
            defaultValue: 'original'
          },
          {
            id: 'includeVariations',
            type: 'checkbox',
            label: 'Include Color Variations',
            description: 'Add lighter and darker shades of each color.',
            validation: {
              required: false
            },
            templateVar: 'includeVariations',
            affectsPreview: true,
            defaultValue: true
          }
        ],
        template: `
          <div class="customization">
            <h3>Customization</h3>
            <p>{{saturation}} saturation with {{lightness}} lightness.</p>
            {{#if includeVariations}}<p>Including color variations for flexibility.</p>{{/if}}
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="customization-preview">
              <p class="text-sm text-gray-600 dark:text-gray-400">{{saturation}} • {{lightness}}</p>
              {{#if includeVariations}}<p class="text-xs text-gray-500">+ Variations</p>{{/if}}
            </div>
          `,
          format: 'html'
        }
      }
    ],
    output: {
      // The component's `downloadPalette` (see
      // `ColorPaletteGenerator.tsx:781-807`) emits `text/plain` for
      // CSS / SCSS / Tailwind / hex-list export formats, and JSON
      // for the JSON export. The data record previously declared
      // `type: 'html'` / `encodingFormat: 'text/html'` which never
      // matched any code path — fixed to declare the most common
      // export shape (`text/plain`) so the JSON-LD `CreateAction`
      // claim through `metadata.ts:1334` is honest. JSON export is
      // a per-`exportFormat` choice that doesn't surface in
      // JSON-LD today; if a future audit wants it represented,
      // promote it to a `variants` entry the way other generators
      // do. From PR #104.
      format: {
        type: 'text',
        encodingFormat: 'text/plain'
      },
      template: `
        <div class="color-palette-document">
          <div class="palette-header">
            <h1>{{colorName}} Color Palette</h1>
            <p class="palette-description">{{harmonyType}} harmony with {{paletteSize}} colors</p>
          </div>

          <div class="palette-preview">
            <div class="color-grid">
              <!-- Generated colors will be inserted here -->
              <div class="color-swatch" style="background-color: {{baseColor}};">
                <span class="color-hex">{{baseColor}}</span>
                <span class="color-name">Primary</span>
              </div>
              <!-- Additional colors based on harmony type -->
            </div>
          </div>

          <div class="palette-specifications">
            <h2>Specifications</h2>
            <div class="spec-grid">
              <div class="spec-item">
                <span class="spec-label">Harmony Type:</span>
                <span class="spec-value">{{harmonyType}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Palette Size:</span>
                <span class="spec-value">{{paletteSize}} colors</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Accessibility:</span>
                <span class="spec-value">WCAG {{accessibilityLevel}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Usage:</span>
                <span class="spec-value">{{usageType}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Saturation:</span>
                <span class="spec-value">{{saturation}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Lightness:</span>
                <span class="spec-value">{{lightness}}</span>
              </div>
            </div>
          </div>

          <div class="palette-usage">
            <h2>Usage Guidelines</h2>
            <div class="usage-guidelines">
              <h3>Color Applications</h3>
              <ul>
                {{#each usageType}}
                <li><strong>{{this}}:</strong> Use primary colors for main elements, secondary colors for accents</li>
                {{/each}}
              </ul>
              
              <h3>Accessibility Notes</h3>
              <p>This palette meets WCAG {{accessibilityLevel}} standards. Ensure sufficient contrast when using colors for text and backgrounds.</p>
              
              {{#if includeNeutrals}}
              <h3>Neutral Colors</h3>
              <p>Use neutral colors for backgrounds, borders, and supporting elements to maintain visual hierarchy.</p>
              {{/if}}
            </div>
          </div>

          <div class="palette-footer">
            <p>Generated with AdSights Color Palette Generator | {{formatDate now 'YYYY-MM-DD'}}</p>
          </div>
        </div>
      `,
      filename: '{{colorName}}-color-palette-{{formatDate now "YYYY-MM-DD"}}.html'
    },
    ui: {
      enablePreview: true,
      enableDownload: true,
      previewDelay: 300
    }
  },
  faqs: [
    {
      question: "What is color harmony and why is it important for marketing?",
      answer: "Color harmony refers to the pleasing arrangement of colors that work well together visually. In marketing, color harmony is crucial because it creates professional, cohesive brand experiences that build trust and recognition. Harmonious color palettes improve brand consistency across all touchpoints, enhance visual appeal and engagement, create emotional connections with your audience, improve readability and accessibility, and help differentiate your brand from competitors. Different harmony types serve different purposes: monochromatic schemes create elegant, unified looks; complementary colors create dynamic contrast; analogous colors provide smooth, natural transitions; and triadic schemes offer balanced vibrancy."
    },
    {
      question: "How do I choose the right color harmony for my brand?",
      answer: "Choosing the right color harmony depends on your brand personality, target audience, and marketing goals. Monochromatic schemes work well for luxury, minimalist, or professional brands seeking sophistication. Analogous colors are perfect for natural, calming, or organic brands. Complementary colors create energy and excitement, ideal for dynamic, innovative, or youth-oriented brands. Triadic schemes offer balanced vibrancy for creative, playful, or diverse brands. Consider your brand values: trust and stability favor monochromatic or analogous schemes, while innovation and energy benefit from complementary or triadic schemes. Also consider your industry: financial services often use monochromatic blues, healthcare uses calming analogous greens, and entertainment uses vibrant complementary or triadic schemes."
    },
    {
      question: "What does WCAG accessibility compliance mean for color palettes?",
      answer: "WCAG (Web Content Accessibility Guidelines) compliance ensures your color choices are accessible to people with visual impairments, including color blindness. WCAG AA compliance requires a 4.5:1 contrast ratio for normal text and 3:1 for large text, while WCAG AAA requires 7:1 for normal text and 4.5:1 for large text. This means your text colors must have sufficient contrast against background colors to be readable. Accessible color palettes not only comply with legal requirements but also improve usability for all users, expand your potential audience, enhance SEO performance, and demonstrate social responsibility. Always test your color combinations with accessibility tools and consider how your palette works for users with different types of color vision deficiencies."
    },
    {
      question: "How can I use color palettes effectively in marketing campaigns?",
      answer: "Effective color palette usage in marketing requires strategic application across all touchpoints. Use your primary color for logos, headlines, and key call-to-action buttons to create brand recognition. Secondary colors work well for accents, highlights, and supporting elements. Neutral colors provide balance and prevent visual overwhelm. Apply the 60-30-10 rule: 60% dominant color (usually neutral), 30% secondary color, and 10% accent color. Consider color psychology: blue builds trust, red creates urgency, green suggests growth, and yellow conveys optimism. Test different color combinations in A/B tests to see what resonates with your audience. Ensure consistency across all marketing materials, from digital ads to print collateral, to reinforce brand recognition and professional appearance."
    },
    {
      question: "What's the difference between RGB, HEX, and HSL color formats?",
      answer: "RGB (Red, Green, Blue) uses three values (0-255) to define colors and is ideal for digital displays. HEX codes are hexadecimal representations of RGB values, commonly used in web design and easy to share. HSL (Hue, Saturation, Lightness) is more intuitive for designers as it separates color information from brightness, making it easier to create variations. For marketing purposes, HEX codes are most practical for web and digital applications, RGB values are needed for design software, and HSL is useful when you need to create systematic color variations. Most design tools can convert between these formats automatically. When sharing color palettes with team members or clients, include multiple formats to ensure compatibility across different tools and platforms."
    }
  ],
  relatedResources: [
    {
      id: "subtitle-overlay-generator",
      title: "Subtitle Overlay Generator",
      relationshipDescription: "Create video overlays using your color palette for consistent branding",
      slug: "subtitle-overlay-generator",
      fullRoute: "/resources/tools/generators/subtitle-overlay-generator",
      resourceType: "tool",
      toolType: "generator"
    },
    {
      id: "digital-marketing-buyer-persona-template",
      title: "Digital Marketing Buyer Persona Template",
      relationshipDescription: "Align palette choices with persona-driven brand positioning",
      slug: "digital-marketing-buyer-persona-template",
      fullRoute: "/resources/templates/digital-marketing-buyer-persona-template",
      resourceType: "template"
    },
    {
      id: "creative-quality-grader",
      title: "Creative Quality Grader",
      relationshipDescription: "Analyze your creative designs and get color optimization recommendations",
      slug: "creative-quality-grader",
      fullRoute: "/resources/tools/analyzers/creative-quality-grader",
      resourceType: "tool",
      toolType: "analyzer"
    },
    {
      id: "how-to-spot-a-fake-screenshot",
      title: "How to Spot a Fake Screenshot",
      relationshipDescription: "Keep branded mockups credible — contrast-check palettes against real UI patterns",
      slug: "how-to-spot-a-fake-screenshot",
      fullRoute: "/resources/guides/how-to-spot-a-fake-screenshot",
      resourceType: "guide"
    }
  ]
}
